/** Drag must move past this fraction of the stage width to commit to a neighbour. */
export const COMMIT_FRACTION = 0.3;
/** A flick faster than this (px/ms, sign = direction) commits regardless of distance. */
export const FLICK_VELOCITY = 0.5;
/** Resistance applied when dragging past the first/last photo. */
export const RUBBER_BAND_FACTOR = 0.3;
/** Duration of the eased settle/step tween. */
export const SETTLE_MS = 300;
/** Pixels of movement before the drag direction (horizontal/vertical) locks. */
export const DIRECTION_LOCK_PX = 10;
/** Only samples within this window (ms) of the last contribute to velocity. */
const VELOCITY_WINDOW_MS = 80;

export interface DragSample {
  x: number;
  t: number;
}

/** Average pointer velocity in px/ms over the most recent samples. Positive is
 *  rightward. Returns 0 when there is not enough signal. */
export function estimateVelocity(samples: DragSample[]): number {
  if (samples.length < 2) return 0;
  const last = samples[samples.length - 1];
  // Walk back to find the oldest sample still within the velocity window.
  let i = samples.length - 2;
  while (i > 0 && last.t - samples[i - 1].t <= VELOCITY_WINDOW_MS) i--;
  const first = samples[i];
  const dt = last.t - first.t;
  if (dt <= 0) return 0;
  return (last.x - first.x) / dt;
}

/** Damp a track delta (px) when it would pull past an end. `dx > 0` pulls toward
 *  the previous photo; `dx < 0` toward the next. */
export function rubberBand(dx: number, index: number, count: number): number {
  const pastStart = index === 0 && dx > 0;
  const pastEnd = index === count - 1 && dx < 0;
  return pastStart || pastEnd ? dx * RUBBER_BAND_FACTOR : dx;
}

/** Index to settle on after a release. `dx > 0` moved right (toward previous). */
export function decideCommit(params: {
  dx: number;
  velocity: number;
  stageWidth: number;
  index: number;
  count: number;
}): number {
  const { dx, velocity, stageWidth, index, count } = params;
  const fraction = stageWidth > 0 ? dx / stageWidth : 0;
  const wantsPrev = fraction > COMMIT_FRACTION || velocity > FLICK_VELOCITY;
  const wantsNext = fraction < -COMMIT_FRACTION || velocity < -FLICK_VELOCITY;
  if (wantsPrev && index > 0) return index - 1;
  if (wantsNext && index < count - 1) return index + 1;
  return index;
}

/** Standard ease-out cubic for the settle tween. */
export function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}
